import React, { useMemo, useState, useEffect } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { ResTecnicoAccess } from "../../components/RoleBasedAccess";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useCollection } from "../../hooks/useCollection";
import { useFirestore } from "../../hooks/useFirestore";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import Select from "react-select";

const EMPTY_INTERVENTO = {
  data: "",
  oraInizio: "",
  oraFine: "",
  luogo: "",
};

export default function DashboardReperibilita() {
  const { user } = useAuthContext();
  const isAdmin = useIsAdmin(user);
  const { documents: users } = useCollection("users");
  const { documents: reperibilita, error: reperibilitaError } =
    useCollection("reperibilita");
  const { addDocument, updateDocument, deleteDocument, response } =
    useFirestore("reperibilita");

  const [monthFilter, setMonthFilter] = useState(() => {
    const now = new Date();
    return format(now, "yyyy-MM");
  });

  const [showModal, setShowModal] = useState(false);
  const [editingRep, setEditingRep] = useState(null);
  const [selectedUserOption, setSelectedUserOption] = useState(null);
  const [formData, setFormData] = useState({
    dataInizio: "",
    dataFine: "",
    note: "",
  });
  const [formError, setFormError] = useState("");
  const [portalTarget, setPortalTarget] = useState(null);
  const [selectedSector, setSelectedSector] = useState(null);
  const [detailsInterventi, setDetailsInterventi] = useState([]);
  const [currentIntervento, setCurrentIntervento] = useState(EMPTY_INTERVENTO);

  useEffect(() => {
    const div = document.createElement("div");
    div.className = "dashboard-reperibilita-portal";
    document.body.appendChild(div);
    setPortalTarget(div);
    return () => {
      document.body.removeChild(div);
    };
  }, []);

  const settoreRT = useMemo(() => {
    const me = (users || []).find((u) => u.id === user?.uid);
    return me?.anagrafica?.settore || null;
  }, [users, user]);

  const sectorUserIds = useMemo(() => {
    if (!users) return new Set();

    if (isAdmin) {
      const sel = selectedSector?.value || null;
      if (!sel || sel === "__ALL__" || sel === "ROBY") {
        return new Set(users.map((u) => u.id));
      }
      return new Set(
        users
          .filter((u) => {
            const s = u.anagrafica?.settore;
            if (!s) return false;
            return s === sel;
          })
          .map((u) => u.id),
      );
    }

    if (!settoreRT) return new Set();

    if (settoreRT === "ROBY") {
      return new Set(users.map((u) => u.id));
    }

    return new Set(
      users.filter((u) => u.anagrafica?.settore === settoreRT).map((u) => u.id),
    );
  }, [users, settoreRT, isAdmin, selectedSector]);

  const filteredReperibilita = useMemo(() => {
    if (!reperibilita || !sectorUserIds || sectorUserIds.size === 0) return [];

    let list = reperibilita.filter((rep) => sectorUserIds.has(rep.userId));

    if (monthFilter) {
      const monthStart = new Date(monthFilter + "-01");
      const monthEnd = new Date(
        monthStart.getFullYear(),
        monthStart.getMonth() + 1,
        0,
      );
      list = list.filter((rep) => {
        const start = rep.dataInizio?.toDate ? rep.dataInizio.toDate() : null;
        const end = rep.dataFine?.toDate ? rep.dataFine.toDate() : null;
        if (!start || !end) return false;
        if (end < monthStart || start > monthEnd) return false;
        return true;
      });
    }

    return list.slice().sort((a, b) => {
      const dateA = a.dataInizio?.toDate ? a.dataInizio.toDate() : new Date(0);
      const dateB = b.dataInizio?.toDate ? b.dataInizio.toDate() : new Date(0);
      return dateA - dateB;
    });
  }, [reperibilita, sectorUserIds, monthFilter]);

  const formatDate = (ts) => {
    if (!ts) return "-";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return format(d, "dd/MM/yyyy", { locale: it });
  };

  const getDurationDays = (rep) => {
    const start = rep.dataInizio?.toDate ? rep.dataInizio.toDate() : null;
    const end = rep.dataFine?.toDate ? rep.dataFine.toDate() : null;
    if (!start || !end) return "-";
    const startDay = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate(),
    );
    const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    const diffTime = endDay - startDay;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return `${diffDays} giorni`;
  };

  const getUserName = (userId) => {
    const u = (users || []).find((usr) => usr.id === userId);
    if (!u) return "-";
    if (u.anagrafica) {
      const nome = u.anagrafica.nome || "";
      const cognome = u.anagrafica.cognome || "";
      const full = `${nome} ${cognome}`.trim();
      if (full.length > 0) return full;
    }
    if (u.displayName) return u.displayName;
    return u.email || "-";
  };

  const userOptions = useMemo(() => {
    if (!users || !sectorUserIds || sectorUserIds.size === 0) return [];
    return users
      .filter((u) => sectorUserIds.has(u.id))
      .map((u) => {
        let label = "";
        if (u.anagrafica) {
          const nome = u.anagrafica.nome || "";
          const cognome = u.anagrafica.cognome || "";
          label = `${nome} ${cognome}`.trim();
        }
        if (!label && u.displayName) {
          label = u.displayName;
        }
        if (!label) {
          label = u.email || u.id;
        }
        return {
          value: u.id,
          label,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [users, sectorUserIds]);

  const sectorOptions = useMemo(() => {
    if (!users) return [];
    const codes = new Set();
    users.forEach((u) => {
      let code = u.anagrafica?.settore;
      if (typeof code === "string") {
        code = code.trim();
      }
      if (code) {
        codes.add(code);
      }
    });
    const list = Array.from(codes).sort();
    const options = list.map((code) => ({
      value: code,
      label: code,
    }));
    return [{ value: "__ALL__", label: "Tutti i settori" }, ...options];
  }, [users]);

  const openCreateModal = () => {
    setEditingRep(null);
    setSelectedUserOption(null);
    setFormData({
      dataInizio: "",
      dataFine: "",
      note: "",
    });
    setDetailsInterventi([]);
    setCurrentIntervento(EMPTY_INTERVENTO);
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = (rep) => {
    const opt = userOptions.find((o) => o.value === rep.userId) || null;
    setEditingRep(rep);
    setSelectedUserOption(opt);
    const start = rep.dataInizio?.toDate ? rep.dataInizio.toDate() : null;
    const end = rep.dataFine?.toDate ? rep.dataFine.toDate() : null;
    setFormData({
      dataInizio: start ? format(start, "yyyy-MM-dd") : "",
      dataFine: end ? format(end, "yyyy-MM-dd") : "",
      note: rep.note || "",
    });
    setDetailsInterventi(
      Array.isArray(rep.interventi)
        ? rep.interventi.map((item) => ({
            data: item?.data || "",
            oraInizio: item?.oraInizio || "",
            oraFine: item?.oraFine || "",
            luogo: item?.luogo || "",
          }))
        : [],
    );
    setCurrentIntervento(EMPTY_INTERVENTO);
    setFormError("");
    setShowModal(true);
  };

  const handleInterventoDraftChange = (e) => {
    const { name, value } = e.target;
    setCurrentIntervento((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInterventoChange = (index, field, value) => {
    setDetailsInterventi((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const addIntervento = () => {
    if (
      !currentIntervento.data ||
      !currentIntervento.oraInizio ||
      !currentIntervento.oraFine ||
      !currentIntervento.luogo.trim()
    ) {
      setFormError("Per aggiungere un intervento compila data, orari e luogo.");
      return;
    }

    setDetailsInterventi((prev) => [
      ...prev,
      {
        data: currentIntervento.data,
        oraInizio: currentIntervento.oraInizio,
        oraFine: currentIntervento.oraFine,
        luogo: currentIntervento.luogo.trim(),
      },
    ]);
    setCurrentIntervento(EMPTY_INTERVENTO);
    setFormError("");
  };

  const removeIntervento = (index) => {
    setDetailsInterventi((prev) =>
      prev.filter((_, itemIndex) => itemIndex !== index),
    );
  };

  const hasInvalidInterventi = detailsInterventi.some(
    (item) =>
      !item?.data ||
      !item?.oraInizio ||
      !item?.oraFine ||
      !String(item?.luogo || "").trim(),
  );

  const handleDelete = async (rep) => {
    const ok = window.confirm(
      "Sei sicuro di voler eliminare questa reperibilità?",
    );
    if (!ok) return;
    await deleteDocument(rep.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!selectedUserOption) {
      setFormError("Seleziona un dipendente.");
      return;
    }

    if (!formData.dataInizio || !formData.dataFine) {
      setFormError("Le date di inizio e fine sono obbligatorie.");
      return;
    }

    const startDate = new Date(formData.dataInizio + "T00:00:00");
    const endDate = new Date(formData.dataFine + "T23:59:59");

    if (startDate > endDate) {
      setFormError("La data di inizio non può essere successiva alla fine.");
      return;
    }

    try {
      const payload = {
        userId: selectedUserOption.value,
        dataInizio: startDate,
        dataFine: endDate,
        note: formData.note,
      };

      if (editingRep) {
        if (hasInvalidInterventi) {
          setFormError(
            "Ogni intervento deve avere data, ora inizio, ora fine e luogo compilati.",
          );
          return;
        }
        await updateDocument(editingRep.id, {
          ...payload,
          interventi: detailsInterventi,
        });
      } else {
        await addDocument(payload);
      }

      setShowModal(false);
      setEditingRep(null);
      setSelectedUserOption(null);
      setFormData({
        dataInizio: "",
        dataFine: "",
        note: "",
      });
      setDetailsInterventi([]);
      setCurrentIntervento(EMPTY_INTERVENTO);
    } catch (err) {
      setFormError("Errore durante il salvataggio della reperibilità.");
    }
  };

  const displaySector =
    settoreRT === "ROBY" ? "Tutti i settori" : settoreRT || "Non assegnato";

  const todayOnCall = useMemo(() => {
    if (!filteredReperibilita || filteredReperibilita.length === 0) return [];
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return filteredReperibilita.filter((rep) => {
      const start = rep.dataInizio?.toDate ? rep.dataInizio.toDate() : null;
      const end = rep.dataFine?.toDate ? rep.dataFine.toDate() : null;
      if (!start || !end) return false;
      const startDay = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate(),
      );
      const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      return startDay <= today && endDay >= today;
    });
  }, [filteredReperibilita]);

  if (!settoreRT && !isAdmin) {
    return (
      <ResTecnicoAccess>
        <div
          style={{
            padding: "24px 16px",
          }}
        >
          <p>
            Non hai un settore assegnato. Contatta l&apos;amministrazione per
            completare la tua anagrafica.
          </p>
        </div>
      </ResTecnicoAccess>
    );
  }

  return (
    <ResTecnicoAccess>
      <div
        className="page-container"
        style={{ padding: "24px 16px", backgroundColor: "#f5f7fb" }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ marginBottom: "24px" }}>
            <h1 className="page-title" style={{ marginBottom: "4px" }}>
              Dashboard Reperibilità
            </h1>
            <p
              style={{
                margin: 0,
                marginBottom: "8px",
                color: "#555",
                maxWidth: "640px",
              }}
            >
              Visualizza i periodi di reperibilità assegnati ai dipendenti del
              tuo settore. I dati sono filtrati per mese e ordinati per data di
              inizio.
            </p>
            <p
              style={{
                margin: 0,
                color: "#777",
                fontSize: "0.9rem",
              }}
            >
              Settore di riferimento: <strong>{displaySector}</strong>
            </p>
          </div>

          <div
            style={{
              marginBottom: "16px",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-end",
              gap: "12px",
            }}
          >
            <label
              style={{
                fontSize: "0.9rem",
                color: "#555",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              Mese:
              <input
                type="month"
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
                style={{
                  padding: "6px 8px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  fontSize: "0.9rem",
                }}
              />
            </label>
            {isAdmin && (
              <div style={{ minWidth: "220px" }}>
                <label
                  style={{
                    fontSize: "0.9rem",
                    color: "#555",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Settore
                </label>
                <Select
                  classNamePrefix="react-select"
                  value={selectedSector}
                  onChange={(opt) => setSelectedSector(opt)}
                  options={sectorOptions}
                  placeholder="Tutti i settori"
                  isClearable
                  menuPortalTarget={portalTarget}
                  styles={{
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                />
              </div>
            )}
            <button
              type="button"
              onClick={openCreateModal}
              style={{
                padding: "8px 14px",
                borderRadius: "4px",
                border: "none",
                backgroundColor: "#1976d2",
                color: "white",
                fontSize: "0.9rem",
                cursor: "pointer",
              }}
            >
              Nuova reperibilità
            </button>
          </div>

          {todayOnCall.length > 0 && (
            <div
              style={{
                marginBottom: "12px",
                padding: "10px 12px",
                borderRadius: "6px",
                backgroundColor: "#e3f2fd",
                border: "1px solid #90caf9",
                fontSize: "0.9rem",
              }}
            >
              <strong>Reperibilità di oggi:</strong>{" "}
              {todayOnCall.map((rep, idx) => {
                const name = getUserName(rep.userId);
                const start = formatDate(rep.dataInizio);
                const end = formatDate(rep.dataFine);
                return (
                  <span key={rep.id}>
                    {idx > 0 && ", "}
                    {name} ({start} - {end})
                  </span>
                );
              })}
            </div>
          )}

          <div className="table-container">
            {reperibilitaError && (
              <div
                style={{
                  marginBottom: "8px",
                  color: "#d32f2f",
                  fontSize: "0.9rem",
                }}
              >
                Errore nel caricamento della reperibilità: {reperibilitaError}
              </div>
            )}

            <table className="data-table">
              <thead>
                <tr>
                  <th>Dipendente</th>
                  <th>Data Inizio</th>
                  <th>Data Fine</th>
                  <th>Durata</th>
                  <th>Note</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredReperibilita.map((rep) => (
                  <tr key={rep.id}>
                    <td>{getUserName(rep.userId)}</td>
                    <td>{formatDate(rep.dataInizio)}</td>
                    <td>{formatDate(rep.dataFine)}</td>
                    <td>{getDurationDays(rep)}</td>
                    <td>{rep.note || "-"}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => openEditModal(rep)}
                        style={{
                          marginRight: "6px",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "none",
                          backgroundColor: "#1976d2",
                          color: "white",
                          fontSize: "0.8rem",
                          cursor: "pointer",
                        }}
                      >
                        Modifica
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(rep)}
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "none",
                          backgroundColor: "#d32f2f",
                          color: "white",
                          fontSize: "0.8rem",
                          cursor: "pointer",
                        }}
                      >
                        Elimina
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredReperibilita.length === 0 && (
              <div
                style={{
                  marginTop: "12px",
                  fontSize: "0.9rem",
                  color: "#777",
                }}
              >
                Nessuna reperibilità trovata per il mese selezionato.
              </div>
            )}
          </div>
        </div>
      </div>
      {showModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "20px",
              maxWidth: editingRep ? "820px" : "480px",
              width: "100%",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "1.1rem" }}>
                {editingRep ? "Modifica Reperibilità" : "Nuova Reperibilità"}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: "1.2rem",
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: "10px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "4px",
                    fontSize: "0.9rem",
                  }}
                >
                  Dipendente
                </label>
                <Select
                  classNamePrefix="react-select"
                  value={selectedUserOption}
                  onChange={(opt) => setSelectedUserOption(opt)}
                  options={userOptions}
                  placeholder="Seleziona dipendente"
                  isSearchable
                  menuPortalTarget={portalTarget}
                  styles={{
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  marginBottom: "10px",
                }}
              >
                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "4px",
                      fontSize: "0.9rem",
                    }}
                  >
                    Data inizio
                  </label>
                  <input
                    type="date"
                    value={formData.dataInizio}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        dataInizio: e.target.value,
                      }))
                    }
                    style={{
                      width: "100%",
                      padding: "6px 8px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      fontSize: "0.9rem",
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "4px",
                      fontSize: "0.9rem",
                    }}
                  >
                    Data fine
                  </label>
                  <input
                    type="date"
                    value={formData.dataFine}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        dataFine: e.target.value,
                      }))
                    }
                    style={{
                      width: "100%",
                      padding: "6px 8px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      fontSize: "0.9rem",
                    }}
                  />
                </div>
              </div>
              <div style={{ marginBottom: "10px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "4px",
                    fontSize: "0.9rem",
                  }}
                >
                  Note
                </label>
                <textarea
                  rows={3}
                  value={formData.note}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      note: e.target.value,
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    fontSize: "0.9rem",
                    resize: "vertical",
                  }}
                />
              </div>
              {editingRep && (
                <div
                  style={{
                    marginBottom: "14px",
                    padding: "12px",
                    borderRadius: "6px",
                    backgroundColor: "#f5f7fb",
                    border: "1px solid #dbe3f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "10px",
                      marginBottom: "8px",
                    }}
                  >
                    <strong style={{ fontSize: "0.95rem" }}>
                      Interventi di reperibilità ({detailsInterventi.length})
                    </strong>
                    <span style={{ fontSize: "0.82rem", color: "#555" }}>
                      Puoi modificarli o aggiungerne di nuovi prima del
                      salvataggio
                    </span>
                  </div>

                  {detailsInterventi.length === 0 ? (
                    <div
                      style={{
                        fontSize: "0.88rem",
                        color: "#666",
                        marginBottom: "12px",
                      }}
                    >
                      Nessun intervento associato a questa reperibilità.
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        marginBottom: "12px",
                      }}
                    >
                      {detailsInterventi.map((intervento, index) => (
                        <div
                          key={`${editingRep.id}-intervento-${index}`}
                          style={{
                            border: "1px solid #d7dde8",
                            borderRadius: "6px",
                            padding: "10px",
                            backgroundColor: "#fff",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "8px",
                            }}
                          >
                            <strong style={{ fontSize: "0.88rem" }}>
                              Intervento {index + 1}
                            </strong>
                            <button
                              type="button"
                              onClick={() => removeIntervento(index)}
                              style={{
                                padding: "4px 8px",
                                borderRadius: "4px",
                                border: "none",
                                backgroundColor: "#d32f2f",
                                color: "#fff",
                                fontSize: "0.8rem",
                                cursor: "pointer",
                              }}
                            >
                              Rimuovi
                            </button>
                          </div>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                              gap: "10px",
                            }}
                          >
                            <div>
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "4px",
                                  fontSize: "0.82rem",
                                }}
                              >
                                Data
                              </label>
                              <input
                                type="date"
                                value={intervento.data || ""}
                                onChange={(e) =>
                                  handleInterventoChange(
                                    index,
                                    "data",
                                    e.target.value,
                                  )
                                }
                                style={{
                                  width: "100%",
                                  padding: "6px 8px",
                                  borderRadius: "4px",
                                  border: "1px solid #ccc",
                                  fontSize: "0.85rem",
                                }}
                              />
                            </div>
                            <div>
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "4px",
                                  fontSize: "0.82rem",
                                }}
                              >
                                Luogo
                              </label>
                              <input
                                type="text"
                                value={intervento.luogo || ""}
                                onChange={(e) =>
                                  handleInterventoChange(
                                    index,
                                    "luogo",
                                    e.target.value,
                                  )
                                }
                                placeholder="Luogo intervento"
                                style={{
                                  width: "100%",
                                  padding: "6px 8px",
                                  borderRadius: "4px",
                                  border: "1px solid #ccc",
                                  fontSize: "0.85rem",
                                }}
                              />
                            </div>
                            <div>
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "4px",
                                  fontSize: "0.82rem",
                                }}
                              >
                                Ora inizio
                              </label>
                              <input
                                type="time"
                                value={intervento.oraInizio || ""}
                                onChange={(e) =>
                                  handleInterventoChange(
                                    index,
                                    "oraInizio",
                                    e.target.value,
                                  )
                                }
                                style={{
                                  width: "100%",
                                  padding: "6px 8px",
                                  borderRadius: "4px",
                                  border: "1px solid #ccc",
                                  fontSize: "0.85rem",
                                }}
                              />
                            </div>
                            <div>
                              <label
                                style={{
                                  display: "block",
                                  marginBottom: "4px",
                                  fontSize: "0.82rem",
                                }}
                              >
                                Ora fine
                              </label>
                              <input
                                type="time"
                                value={intervento.oraFine || ""}
                                onChange={(e) =>
                                  handleInterventoChange(
                                    index,
                                    "oraFine",
                                    e.target.value,
                                  )
                                }
                                style={{
                                  width: "100%",
                                  padding: "6px 8px",
                                  borderRadius: "4px",
                                  border: "1px solid #ccc",
                                  fontSize: "0.85rem",
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div
                    style={{
                      borderTop: "1px solid #dbe3f0",
                      paddingTop: "12px",
                    }}
                  >
                    <strong
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "0.9rem",
                      }}
                    >
                      Nuovo intervento
                    </strong>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        gap: "10px",
                        marginBottom: "10px",
                      }}
                    >
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "4px",
                            fontSize: "0.82rem",
                          }}
                        >
                          Data
                        </label>
                        <input
                          type="date"
                          name="data"
                          value={currentIntervento.data}
                          onChange={handleInterventoDraftChange}
                          style={{
                            width: "100%",
                            padding: "6px 8px",
                            borderRadius: "4px",
                            border: "1px solid #ccc",
                            fontSize: "0.85rem",
                          }}
                        />
                      </div>
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "4px",
                            fontSize: "0.82rem",
                          }}
                        >
                          Luogo
                        </label>
                        <input
                          type="text"
                          name="luogo"
                          value={currentIntervento.luogo}
                          onChange={handleInterventoDraftChange}
                          placeholder="Luogo intervento"
                          style={{
                            width: "100%",
                            padding: "6px 8px",
                            borderRadius: "4px",
                            border: "1px solid #ccc",
                            fontSize: "0.85rem",
                          }}
                        />
                      </div>
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "4px",
                            fontSize: "0.82rem",
                          }}
                        >
                          Ora inizio
                        </label>
                        <input
                          type="time"
                          name="oraInizio"
                          value={currentIntervento.oraInizio}
                          onChange={handleInterventoDraftChange}
                          style={{
                            width: "100%",
                            padding: "6px 8px",
                            borderRadius: "4px",
                            border: "1px solid #ccc",
                            fontSize: "0.85rem",
                          }}
                        />
                      </div>
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "4px",
                            fontSize: "0.82rem",
                          }}
                        >
                          Ora fine
                        </label>
                        <input
                          type="time"
                          name="oraFine"
                          value={currentIntervento.oraFine}
                          onChange={handleInterventoDraftChange}
                          style={{
                            width: "100%",
                            padding: "6px 8px",
                            borderRadius: "4px",
                            border: "1px solid #ccc",
                            fontSize: "0.85rem",
                          }}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={addIntervento}
                      style={{
                        padding: "6px 12px",
                        borderRadius: "4px",
                        border: "none",
                        backgroundColor: "#2e7d32",
                        color: "#fff",
                        fontSize: "0.85rem",
                        cursor: "pointer",
                      }}
                    >
                      Aggiungi intervento
                    </button>
                  </div>
                </div>
              )}
              {formError && (
                <div
                  style={{
                    color: "#d32f2f",
                    fontSize: "0.85rem",
                    marginBottom: "8px",
                  }}
                >
                  {formError}
                </div>
              )}
              {response && response.isPending && (
                <div
                  style={{
                    fontSize: "0.85rem",
                    marginBottom: "8px",
                    color: "#555",
                  }}
                >
                  Salvataggio in corso...
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "8px",
                  marginTop: "8px",
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    backgroundColor: "#f5f5f5",
                    fontSize: "0.9rem",
                    cursor: "pointer",
                  }}
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  style={{
                    padding: "6px 12px",
                    borderRadius: "4px",
                    border: "none",
                    backgroundColor: "#1976d2",
                    color: "white",
                    fontSize: "0.9rem",
                    cursor: "pointer",
                  }}
                >
                  {editingRep ? "Salva modifiche" : "Crea reperibilità"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </ResTecnicoAccess>
  );
}
